import { UserSelector } from '@tencent/tds-ui';
import { Modal, Form, Input, Select, message, Row, Col, Radio, Cascader } from 'antd';
import classNames from 'classnames';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { getUserId, isInternal } from '@/infrastructure/auth';
import { BusiCode } from '@/infrastructure/axios/code';
import { BASE_NAME } from '@/infrastructure/history';
import { getAppIcon, AccessPlatformType } from '@/infrastructure/utils/app';
import { getTips } from '@/presentation/components/issue-list/components/tips';
import { GameEngine, GAME_ENGINE_OPTIONS, IAddProductOaReq, PlatformType, productOAServer } from '@/service/productoa';
import {
  GetDepartmentsByUser,
} from './api';
import styles from './style.module.scss';

export interface AddAppReq {
  rdmProductId: string;
  bundleId: string;
  pid: number;
  name: string;
  type: number;
  logoUrl: string;
  ownerId: string;
  desc: string;
  appFrom: number;
  admins: string[];
  department: string;
  appForm: number;
}

interface FormFieldType {
  productName: string;
  bundleId: string;
  department: string;
  admins: string[];
  appType: number[];
  platform: number;
  appForm: number;
  gameEngine: number;
}

interface SelectAppType {
  value?: (number | string)[];
  onChange?: (value: (number | string)[]) => void
}

export enum Id {
  MIG = 954,
  SNG = 955,
  OMG = 957,
  IEG = 956,
  CDG = 953,
  TEG = 958,
  CIO = 9832,
  EI = 1000009,
  WXG = 14129,
  S3 = 2234,
  S1 = 78,
  S2 = 2233,
  WeBank = 88888,
}

export const BG_ID = {
  [Id.MIG]: 1, // MIG
  [Id.SNG]: 4, // SNG
  [Id.OMG]: 6, // OMG
  [Id.IEG]: 7, // IEG
  [Id.CDG]: 9, // CDG
  [Id.TEG]: 11, // TEG
  [Id.CIO]: 13, // 公司级组织
  [Id.EI]: 12, // 企业互联
  [Id.WXG]: 14, // WXG
  [Id.S3]: 15, // S3
  [Id.S1]: 16, // S1
  [Id.S2]: 17, // S2
  [Id.WeBank]: 18, // WeBank
};

export const ALLTYPE_OPTIONS = [
  { label: i18n.t('软件'), value: 1 },
  { label: i18n.t('游戏'), value: 2 },
  { label: i18n.t('办公商务'), value: 3 },
  { label: i18n.t('聊天通讯'), value: 4 },
  { label: i18n.t('系统输入'), value: 5 },
  { label: i18n.t('手机美化'), value: 6 },
  { label: i18n.t('阅读学习'), value: 7 },
  { label: i18n.t('出行地图'), value: 8 },
  { label: i18n.t('影音视听'), value: 9 },
  { label: i18n.t('儿童亲子'), value: 10 },
  { label: i18n.t('拍摄美化'), value: 11 },
  { label: i18n.t('购物时尚'), value: 12 },
  { label: i18n.t('生活娱乐'), value: 13 },
  { label: i18n.t('实用工具'), value: 14 },
  { label: i18n.t('动作冒险'), value: 15 },
  { label: i18n.t('飞行射击'), value: 16 },
  { label: i18n.t('角色扮演'), value: 17 },
  { label: i18n.t('经营策略'), value: 18 },
  { label: i18n.t('体育竞速'), value: 19 },
  { label: i18n.t('网络游戏'), value: 20 },
  { label: i18n.t('益智游戏'), value: 21 },
  { label: i18n.t('休闲娱乐'), value: 22 },
];

export const PLATFORM_OPTIONS = [
  { label: 'Android', value: PlatformType.ANDROID, type: [AccessPlatformType.App, AccessPlatformType.SDK] },
  { label: 'iOS', value: PlatformType.IPHONE, type: [AccessPlatformType.App, AccessPlatformType.SDK, AccessPlatformType.Extension] },
  { label: 'Mac', value: PlatformType.MAC, type: [AccessPlatformType.App, AccessPlatformType.SDK] },
  { label: 'AppleTV', value: PlatformType.AppleTV, type: [AccessPlatformType.App, AccessPlatformType.SDK] },
  { label: 'Windows', value: PlatformType.WINDOWS, type: [AccessPlatformType.App, AccessPlatformType.SDK] },
  { label: 'Harmony', value: PlatformType.HARMONY, type: [AccessPlatformType.App, AccessPlatformType.SDK] },
  { label: 'Linux', value: PlatformType.LINUX, type: [AccessPlatformType.App, AccessPlatformType.SDK] },
  { label: 'VisionOS', value: PlatformType.VisionOS, type: [AccessPlatformType.App] },
  { label: 'Flutter', value: PlatformType.FLUTTER, type: [AccessPlatformType.Multi] },
  { label: 'Hippy', value: PlatformType.Hippy, type: [AccessPlatformType.Multi] },
  { label: 'Kuikly', value: PlatformType.Kuikly, type: [AccessPlatformType.Multi] },
  { label: i18n.t('小程序'), value: PlatformType.MiniProgram, type: [AccessPlatformType.Multi] },
  { label: 'Electron', value: PlatformType.Electron, type: [AccessPlatformType.Multi] },
  { label: 'React Native', value: PlatformType.ReactNative, type: [AccessPlatformType.Multi] },
];

export const PLATFORM_TYPE_OPTIONS = [
  { label: 'App', value: AccessPlatformType.App },
  { label: 'SDK', value: AccessPlatformType.SDK },
  { label: 'Extension', value: AccessPlatformType.Extension },
  { label: i18n.t('跨端应用'), value: AccessPlatformType.Multi },
];

export const APPFORM_ALL_OPTIONS = [
  { label: 'App', value: 1 },
  { label: 'Extension', value: 3 },
];

export const APPFORM_OPTIONS = [
  { label: 'App', value: 1 },
];

export const ILLEGAL_REG = /^[\u4e00-\u9fa5a-zA-Z0-9=+/@#_-]+$/;
export const BUNDLE_ID_REG = /^[a-zA-Z0-9\\.-]+$/;

export const DESC_REG = /^[\u4e00-\u9fa5a-zA-Z0-9.,#:*$\s@_\-\\()]+$/;

export const CreateApp = (props: { visible: boolean, handleCancel: () => void }) => {
  const { t } = useTranslation();
  const { visible, handleCancel } = props;
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [selectPlatform, setSelectPlatform] = useState<PlatformType>(PlatformType.ANDROID);
  const [formType, setFormType] = useState<AccessPlatformType | null>(null);
  const [form] = Form.useForm();

  const load = async () => {
    const departmentList = await GetDepartmentsByUser();
    if (Array.isArray(departmentList) && (departmentList?.[0]?.fullName ?? '') !== '') {
      const department = departmentList?.[0]?.fullName || '';
      if (department !== '') {
        form.setFieldsValue({ department });
      }
    }
  };

  useEffect(() => {
    if (!isInternal() && visible) {
      load();
    }
  }, [visible]);

  // 组件初始化时自动选中第一个平台类型
  useEffect(() => {
    const availableTypes = getAvailablePlatformTypes(selectPlatform);
    if (availableTypes.length > 0) {
      const firstType = availableTypes[0];
      setFormType(firstType);
      form.setFieldsValue({
        platform: selectPlatform,
        appForm: firstType,
      });
    }
  }, []);

  const handleSubmit = async (values: FormFieldType) => {
    if (!selectPlatform) {
      message.error(t('请选择接入平台'));
      return;
    }
    if (!formType) {
      message.error(t('请选择接入平台类型'));
      return;
    }
    const params: IAddProductOaReq = {
      bundleId: values.bundleId,
      platformId: selectPlatform,
      name: values.productName,
      type: values.appType?.[1],
      ownerId: getUserId(),
      desc: '',
      appForm: formType,
      admins: values.admins,
      department: values.department,
      gameEngine: values.gameEngine ?? GameEngine.None,
    };

    const data = await productOAServer.addProductOa(params);
    if (data.baseRsp?.code === BusiCode.Ok) {
      message.success(t('创建产品成功'));
      handleCancel();
      location.replace(`${BASE_NAME}/overview?productId=${data?.appId}&pid=${params.platformId}`);
    } else if (data.baseRsp?.code === BusiCode.LimitCreateProduct) {
      message.error(t('当前账号创建的产品数量已达上限，如果需要创建更多产品，请联系Bugly小助手。'), 8);
    } else if (data.baseRsp?.code === BusiCode.LimitUserAddProduct) {
      message.error(`${data?.baseRsp?.msg}`, 8);
      setConfirmLoading(false);
    } else if (data.baseRsp?.code === BusiCode.BundleIDRepeat) {
      message.error(`${data?.baseRsp?.msg}`, 8);
    } else {
      message.error(t('创建产品失败，请重试'), 8);
    }

    setConfirmLoading(false);
    return;
  };

  const handleOk = () => {
    setConfirmLoading(true);
    form.validateFields()
      .then((values: FormFieldType) => {
        // 提交
        handleSubmit(values);
      })
      .catch(() => {
        setConfirmLoading(false);
      });
  };

  const AppType = ({ onChange, value = [] }: SelectAppType) => {
    const options = [
      {
        ...ALLTYPE_OPTIONS[0],
        children: ALLTYPE_OPTIONS.slice(2, 14),
      },
      {
        ...ALLTYPE_OPTIONS[1],
        children: ALLTYPE_OPTIONS.slice(14, 22),
      },
    ];
    const handleAppType = (AppTypeValue: (number | string)[], selectedOptions: Record<string, any>[]) => {
      // 当未选择时点击第一列，默认选中第二列的第一条
      if (AppTypeValue?.length === 1 && value?.[0] !== AppTypeValue[0]) {
        AppTypeValue.push(selectedOptions[0]?.children?.[0]?.value);
        onChange?.(AppTypeValue);
      } else if (AppTypeValue?.length === 1 && AppTypeValue?.[0] === value?.[0] && value?.length === 2) {
        AppTypeValue.push(value[1]);
        onChange?.(AppTypeValue);
      }
      if (AppTypeValue?.length === 2) {
        onChange?.(AppTypeValue);
      }
    };

    return <Cascader changeOnSelect onChange={handleAppType} value={value} options={options} placeholder={t('请选择')} />;
  };

  const onPlatformChange = (platform: PlatformType) => {
    setSelectPlatform(platform);

    // 同步到表单字段
    form.setFieldsValue({ platform });
    // 获取该平台可用的平台类型
    const availableTypes = getAvailablePlatformTypes(platform);

    // 直接默认选中第一个可用的平台类型
    if (availableTypes.length > 0) {
      const firstType = availableTypes[0];
      setFormType(firstType);
      form.setFieldsValue({ appForm: firstType });
    }
  };

  const onAppFormChange = (e: any) => {
    const appForm = e.target.value;
    setFormType(appForm);
  };

  // 根据选择的平台获取可用的平台类型选项
  const getAvailablePlatformTypes = (platform?: PlatformType) => {
    const targetPlatform = platform || selectPlatform;
    const selectedPlatformOption = PLATFORM_OPTIONS.find(opt => opt.value === targetPlatform);
    return selectedPlatformOption ? selectedPlatformOption.type : [];
  };

  return (
    <Modal
      title={t('创建产品')}
      width={727}
      visible={visible}
      onOk={handleOk}
      confirmLoading={confirmLoading}
      onCancel={() => handleCancel()}
    >
      <Form form={form} layout={'vertical'}>
        <Form.Item
          label={t('接入平台')}
          name="platform"
          rules={[{ required: true, message: t('请选择接入平台!') }]}
          style={{ marginBottom: 0 }}
        >
          <div className={classNames(styles.platformContainer, 'mb-[16px]')}>
            {PLATFORM_OPTIONS.map((opt) => {
              const checked = selectPlatform === opt.value;
              return (
                <div
                  key={opt.value}
                  className={classNames(styles.platforms, checked ? styles.active : '')}
                  onClick={() => onPlatformChange(opt.value)}
                >
                  <span>{getAppIcon(`${opt.value}`)}</span>
                  <span>{opt.label}</span>
                </div>
              );
            })}
          </div>
        </Form.Item>
        <Form.Item
          name="appForm"
          rules={[{ required: true, message: t('请选择接入平台类型!') }]}
        >
          <Radio.Group onChange={onAppFormChange}>
            {PLATFORM_TYPE_OPTIONS.filter(opt => getAvailablePlatformTypes().includes(opt.value)).map(opt => (
              <Radio key={opt.value} value={opt.value}>
                {opt.label}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label={t('产品名称')}
              name="productName"
              rules={[
                { required: true, message: t('请输入产品名称!') },
                { max: 20, message: t('字数不能超过20个字符!'), type: 'string' },
                { pattern: ILLEGAL_REG, message: t('产品名称含有非法字符!') },
              ]}
            >
              <Input placeholder={t('请输入仅包含中文字符、英文字母、数字以及特殊字符（=、+、/、@、#、_、-）的字符串')} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label={getTips('Bundle ID', t('BundleID，全称 Bundle Identifier ，也叫应用包名，是应用市场用来区分一款应用的唯一标识。Bugly平台使用用户创建产品时填写的BundleID来作为应用标识，与其他平台进行联动。Bugly平台内部，使用平台分配的AppID来作为Bugly产品的唯一标识。强烈建议用户填写正确的BundleID，如果创建产品后需要修改，请联系Bugly小助手。'))}
              name="bundleId"
              rules={[
                { required: true, message: t('请输入你的Bundle ID!') },
                { pattern: BUNDLE_ID_REG, message: t('Bundle ID含有非法字符!') },
              ]}
            >
              <Input placeholder={t('请输入')} />
            </Form.Item>
          </Col>
        </Row>
        {!isInternal() && <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label={t('部门组织架构')}
              name="department"
              rules={[{ required: true, message: '' }]}
            >
              <Input
                disabled
                style={{ marginBottom: '5px', color: 'rgba(0, 0, 0, 0.85)', background: '#fff' }}
              />
            </Form.Item>
          </Col>
        </Row>}
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label={t('产品类型')}
              name="appType"
              rules={[{ required: true, message: '' }]}
            >
              <AppType />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.appType !== cur.appType}>
          {({ getFieldValue }) => {
            const appType = getFieldValue('appType') as number[] | undefined;
            if (appType?.[0] !== 2) {
              return null;
            }
            return (
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item
                    label="游戏引擎"
                    name="gameEngine"
                    initialValue={GameEngine.None}
                  >
                    <Select options={GAME_ENGINE_OPTIONS} placeholder={t('请选择')} />
                  </Form.Item>
                </Col>
              </Row>
            );
          }}
        </Form.Item>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label={t('管理员')}
              name="admins"
              initialValue={[getUserId()]}
              rules={[{ required: true, message: t('请输入管理员名字!') }]}
            >
              <UserSelector placeholder={t('请输入')} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
